import { useCallback, useEffect, useRef, useState } from 'react';

import type { CreateCsvExportRequestDTO } from '@gearbelt/global';

import { ApiService } from '@/services/api-v2';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

const PROGRESS_REQUEST_SENT = 33;
const PROGRESS_REQUEST_ACCEPTED = 66;
const PROGRESS_COMPLETE = 100;

export type CsvExportStatus = 'idle' | 'generating' | 'ready' | 'error';

/**
 * Drives a server-side CSV export and exposes its live status + progress. On
 * completion status becomes `'ready'` and `downloadUrl` holds the presigned URL;
 * the export tab handles opening it (auto + manual). Unmounting or calling
 * `reset` aborts an in-flight poll so it can't set state on a torn-down tree.
 */
export function useCsvExport() {
  const [status, setStatus] = useState<CsvExportStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setStatus('idle');
    setProgress(0);
    setDownloadUrl(null);
  }, []);

  const generate = useCallback(async (request: CreateCsvExportRequestDTO) => {
    // Abort any prior run instead of bailing, so a remount that aborted the first run can restart cleanly.
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;
    setStatus('generating');
    setProgress(PROGRESS_REQUEST_SENT);
    setDownloadUrl(null);

    try {
      const { runId } = await (await ApiService.CreateCsvExport(request, abort.signal)).json();
      if (abort.signal.aborted) return;
      setProgress(PROGRESS_REQUEST_ACCEPTED);

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      while (Date.now() < deadline) {
        if (abort.signal.aborted) return;
        const run = await (await ApiService.GetExportRun(runId, abort.signal)).json();
        if (abort.signal.aborted) return;
        if (run.status === 'completed' && run.downloadUrl) {
          setProgress(PROGRESS_COMPLETE);
          setDownloadUrl(run.downloadUrl);
          setStatus('ready');
          return;
        }
        if (run.status === 'failed') {
          throw new Error(`CSV export run ${runId} failed`);
        }
        // Reserve 100% for completion so the bar never reads done while spinning.
        setProgress(Math.min(Math.round(run.progress ?? PROGRESS_REQUEST_ACCEPTED), PROGRESS_COMPLETE - 1));
        await sleep(POLL_INTERVAL_MS, abort.signal);
      }
      throw new Error(`CSV export run ${runId} timed out`);
    } catch (error) {
      if (abort.signal.aborted) return;
      console.error('CSV export failed', error);
      setStatus('error');
    }
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  return { status, progress, downloadUrl, generate, reset };
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}
